import {
    AXPSummaryData,
    AXPSummaryBySiteData,
    AXPSummaryBySite,
    Customer,
    CustomSiteName,
    Dashlet,
    AxpCloudStatusData,
    Location,
    Point,
    GraphData,
    StreamGraph
} from '@models/index';
import { Subject } from 'rxjs';

interface Document {
    id: string;
    name: string;
    timestamp: string;
}

interface AXPSummaryDocument extends Document {
    data: { SummaryStats: string; ReportEndTimestampUtc: any }[];
}

interface AXPEquipment {
    label: string;
    value: string;
}

interface AxpServiceStatusDocument extends Document {
    data: { [key: string]: string }[]; // Array of key-value pairs
}

export class DashletAXPCloudHealthSummary extends Dashlet {
    public customer: Customer;
    public service: Location;
    public equipment: AXPEquipment[] = [];
    public pointArray: Point[] = [];
    public chartDataUpdated: Subject<AXPSummaryBySite> = new Subject();
    public settingsChanges: Subject<any> = new Subject();
    public hourSetting: number;
    public summaryPeriod = { value: 0 };
    public lastUpdatedDataAt: Date = new Date(0);
    public periodDataIsShowing: Date = new Date(0);
    public nameTag: string;
    public sizeChange: Subject<number> = new Subject<number>();
    public customSiteNames: CustomSiteName[] = [];
    public loadingDocuments: boolean = true;
    private axpSessionsData: AXPSummaryBySite = { All: [] };
    public axpSessionGraphData: { [key: string]: GraphData[] } = { All: [] };
    public showPSTNCallsOnly: boolean = false;
    public dataMap: Map<string, AXPSummaryDocument> = new Map();

    public readonly commandTypeIdAXPCloudHealthSummary = 'E92588DB-7BA1-46AE-9BB4-C91D0E083CFD';
    public readonly commandTypeIdAXPCloudPSTNHealthSummary = '';
    public readonly commandTypeIdAxpCloudSubscriptions = '4f44decc-27e4-4934-a6f2-4661eca08aec';
    constructor() {
        super();
        this.sizes = [
            {
                id: 0,
                label: 'Small',
                cols: 10,
                rows: 11
            },
            {
                id: 1,
                label: 'Medium',
                cols: 13,
                rows: 11
            }
        ];
        this.applySize(0);
        this.resetData();
        this.summaryPeriod = { value: 0 };
    }

    applySettings(v: { [key: string]: any }) {
        const previousSettings = {
            customer: this.customer ? this.customer.customerId : '',
            service: this.service ? this.service.locationId : '',
            equipment: this.equipment ? this.equipment : [],
            summaryPeriod: this.summaryPeriod,
            showPSTNCallsOnly: this.showPSTNCallsOnly,
            customSiteNames: this.customSiteNames
        };

        super.applySettings(v);
        this.configured = !!(v.customer && v.service && v.equipment.length && v.summaryPeriod);
        if (!this.configured) {
            this.settingsChanges.next(null);
        }

        this.hourSetting = v.summaryPeriod ? Number(v.summaryPeriod.value) : null;
        this.summaryPeriod = v.summaryPeriod;
        this.showPSTNCallsOnly = v.showPSTNCallsOnly;
        if (v.customer) {
            this.customer = new Customer(v.customer.value, v.customer.label);
        } else {
            this.customer = new Customer('', '');
        }
        if (this.configured) {
            this.service = new Location(v.service.value, v.service.label);
        }

        this.service = v.service ? new Location(v.service.value, v.service.label) : new Location('', '');

        this.nameTag = this.configured ? `${v.service.label}` : 'Unconfigured';

        this.customNameTag = v.nameTag;

        if (v.equipment?.length) {
            this.equipment = v.equipment.filter(names => names.label !== 'Unknown');
        }
        if (v.customSiteNames) {
            this.customSiteNames = v.customSiteNames.filter(names => names.label !== 'Unknown');
        }

        this.updateSize();

        const currentSettings = {
            customer: this.customer.customerId,
            service: this.service.locationId,
            equipment: this.equipment,
            summaryPeriod: this.summaryPeriod,
            showPSTNCallsOnly: this.showPSTNCallsOnly,
            customSiteNames: this.customSiteNames
        };

        if (this.areSettingsDifferent(previousSettings, currentSettings)) {
            this.settingsChanges.next(currentSettings);
        }
    }

    private areSettingsDifferent(prev: any, curr: any): boolean {
        return JSON.stringify(prev) !== JSON.stringify(curr);
    }

    public areSettingsValid(settings: any): boolean {
        return this.customer && this.service.locationId && this.equipment?.length && settings.summaryPeriod;
    }

    applySize(id: number) {
        super.applySize(id);
        this.sizeChange.next(id);
        this.updateSize();
        this.refreshNameTag();
    }

    refreshNameTag() {
        if (
            (!this.lastUpdatedDataAt && this.lastUpdatedDataAt.getTime() === 0) ||
            (!this.periodDataIsShowing && this.periodDataIsShowing.getTime() === 0)
        ) {
            return;
        }

        const originalDate = new Date(this.lastUpdatedDataAt);
        const date = new Date(this.lastUpdatedDataAt);
        const month = originalDate.toLocaleDateString(undefined, { month: 'short' });
        const day = date.getDate().toString().padStart(2, '0');
        const timeString = this.extractTime(date);

        const time = this.periodDataIsShowing
            .toLocaleTimeString(undefined, { hour: 'numeric', minute: 'numeric' })
            .toUpperCase();

        const firstDateVal: Date = new Date(this.periodDataIsShowing);
        firstDateVal.setHours(firstDateVal.getHours() - +this.summaryPeriod?.value);
        const firstTime = firstDateVal
            .toLocaleTimeString(undefined, { hour: 'numeric', minute: 'numeric' })
            .toUpperCase();

        let period = '';
        period = firstTime + ' - ' + time;

        if (this.service !== undefined && this.equipment !== undefined && this.summaryPeriod !== undefined) {
            this.generatedNameTag = `${this.nameTag} | ${period} | Last updated at ${day}-${month} ${timeString}`;
        }
    }

    private extractTime(date: Date): string {
        const hours24 = date.getHours(); // Use local time
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const isPM = hours24 >= 12;
        const hours12 = hours24 % 12 || 12; // Convert 24-hour format to 12-hour format
        const period = isPM ? 'PM' : 'AM';
        const timeString = `${hours12}:${minutes} ${period}`;
        return timeString;
    }

    private updateSize() {
        let h = 0;
        let w = 0;
        this.applySizeExpansion(w, h);
    }

    public refreshChartData() {
        this.chartDataUpdated.next(this.axpSessionsData);
        this.refreshNameTag();
    }

    private initNodeData(): StreamGraph {
        return {
            data: {
                streams: 0,
                timeStamp: null
            }
        } as StreamGraph;
    }

    public processIncomingData(newDoc: AXPSummaryDocument) {
        const timeStamp = JSON.parse(newDoc.data[0].ReportEndTimestampUtc);
        if (this.dataMap.has(timeStamp)) {
            return;
        }
        this.dataMap.set(timeStamp, newDoc);
        const targetSize = this.hourSetting * 4;
        const tolerancePercentage = 10;

        const lowerBound = targetSize - targetSize * (tolerancePercentage / 100);
        const upperBound = targetSize + targetSize * (tolerancePercentage / 100);
        if (this.dataMap.size > targetSize) {
            const oldestTimestamp = this.dataMap.keys().next().value;
            this.dataMap.delete(oldestTimestamp);
        }
        this.processAxpSessionData(newDoc.data[0].SummaryStats, new Date(timeStamp));
        if (this.dataMap.size >= lowerBound && this.dataMap.size <= upperBound) {
            this.loadingDocuments = false;
        }
    }

    public processAxpSessionData(axpSessionSummary: any, documentTimestamp: Date): void {
        if (axpSessionSummary) this.loadingDocuments = false;
        const adjustedTimestamp = new Date(documentTimestamp);
        const parsedAxpSiteData: AXPSummaryBySiteData[] = JSON.parse(axpSessionSummary);
        const aggregatedSiteData: { [siteName: string]: any } = {};
        let removedTimestamp: string = '';
        this.equipment.forEach(element => {
            const equipmentData = parsedAxpSiteData[element.value];
            if (!equipmentData || !equipmentData[0] || !equipmentData[0]['All']) return;

            equipmentData.forEach(siteData => {
                Object.keys(siteData).forEach(siteName => {
                    if (!aggregatedSiteData[siteName]) {
                        aggregatedSiteData[siteName] = {
                            ...siteData[siteName],
                            DocumentCreation: adjustedTimestamp
                        };
                    } else {
                        aggregatedSiteData[siteName] = this.aggregateSiteData(
                            aggregatedSiteData[siteName],
                            siteData[siteName]
                        );
                    }
                });
            });
        });

        if (Object.keys(aggregatedSiteData).length === 0) return;

        const pointExists = this.pointArray.some(point => point.x.toISOString() === adjustedTimestamp.toISOString());
        if (!pointExists) {
            let oldestPointIndex = 0;
            let oldestPointTime = this.pointArray[0]?.x.getTime();
            for (let i = 1; i < this.pointArray.length; i++) {
                if (this.pointArray[i].x.getTime() < oldestPointTime) {
                    oldestPointTime = this.pointArray[i].x.getTime();
                    oldestPointIndex = i;
                }
            }
            if (this.pointArray.length >= this.hourSetting * 4) {
                removedTimestamp = this.pointArray[oldestPointIndex].x.toISOString();
                this.pointArray.splice(oldestPointIndex, 1);
            }
            this.pointArray.push({ x: adjustedTimestamp, y: 0 });
        }

        if (removedTimestamp) {
            Object.keys(this.axpSessionsData).forEach(siteName => {
                this.axpSessionsData[siteName] = this.axpSessionsData[siteName].filter(
                    record => new Date(record.DocumentCreation).toISOString() !== removedTimestamp
                );
            });
        }

        const updatedSites: Set<string> = new Set();
        Object.keys(aggregatedSiteData).forEach(siteName => {
            const siteRecords = this.axpSessionsData[siteName] || [];
            const previousRecord = siteRecords[siteRecords.length - 1];

            if (previousRecord?.DocumentCreation.getTime() === adjustedTimestamp.getTime()) return;

            siteRecords.push(aggregatedSiteData[siteName]);
            this.axpSessionsData[siteName] = siteRecords;
            updatedSites.add(siteName);
        });

        updatedSites.forEach(siteName => {
            const siteRecords = this.axpSessionsData[siteName];
            this.buildGraphData(siteRecords, siteName, this.pointArray);
        });

        this.pointArray.sort((a: Point, b: Point) =>
            a.x.getTime() < b.x.getTime() ? 1 : b.x.getTime() < a.x.getTime() ? -1 : 0
        );
    }

    private buildGraphData(axpRecordsData: AXPSummaryData[], siteName: string, pointArray: Point[]): void {
        const graphData: GraphData[] = [];

        const recordMap = new Map<string, AXPSummaryData>();
        axpRecordsData.forEach(record => {
            const timestamp = new Date(record.DocumentCreation).toISOString();
            recordMap.set(timestamp, record);
        });

        pointArray.forEach(point => {
            const timestamp = point.x.toISOString();
            const record = recordMap.get(timestamp);

            const goodStream = this.initNodeData();
            const satisfactoryStream = this.initNodeData();
            const poorStream = this.initNodeData();

            if (record) {
                goodStream.data.streams += record.StreamQuality.Good || 0;
                satisfactoryStream.data.streams += record.StreamQuality.Satisfactory || 0;
                poorStream.data.streams += record.StreamQuality.Bad || 0;

                goodStream.data.timeStamp = point.x;
                satisfactoryStream.data.timeStamp = point.x;
                poorStream.data.timeStamp = point.x;
            } else {
                goodStream.data.streams = 0;
                satisfactoryStream.data.streams = 0;
                poorStream.data.streams = 0;

                goodStream.data.timeStamp = point.x;
                satisfactoryStream.data.timeStamp = point.x;
                poorStream.data.timeStamp = point.x;
            }

            graphData.push({
                Good: goodStream,
                Satisfactory: satisfactoryStream,
                Poor: poorStream
            });
        });

        this.axpSessionGraphData[siteName] = graphData;
    }

    public resetData(): void {
        this.dataMap = new Map();
        this.axpSessionsData = { All: [] };
        this.axpSessionGraphData = { All: [] };
        this.pointArray = [];
    }

    public dispose() {
        this.settingsChanges.complete();
        this.settingsChanges = new Subject();
    }

    private readonly AxpServiceStatus = {
        Operational: { status: 'Operational', colour: 'green' },
        DegradedPerformance: { status: 'Degraded Performance', colour: 'amber' },
        PartialOutage: { status: 'Partial Outage', colour: 'amber' },
        MajorOutage: { status: 'Major Outage', colour: 'red' },
        UnderMaintenance: { status: 'Under Maintenance', colour: 'blueDark' },
        NotApplicable: { status: 'Not Applicable', colour: 'grey' },
        Unknown: { status: 'Unknown', colour: 'grey' }
    };

    public processAxpServiceStatusDocument(newDoc: AxpServiceStatusDocument): AxpCloudStatusData[] {
        if (!newDoc || !newDoc[0] || !newDoc[0].data || newDoc[0].data.length === 0) {
            return [];
        }
        const services: AxpCloudStatusData[] = [];

        for (const [service, status] of Object.entries(newDoc[0].data[0])) {
            services.push({
                Service: service,
                Status: this.AxpServiceStatus[status as string].status,
                Colour: this.AxpServiceStatus[status as string].colour
            });
        }

        return services;
    }

    private aggregateSiteData(existingData: any, newData: any): any {
        const aggregatedData = { ...existingData };
        Object.keys(newData).forEach(key => {
            if (typeof newData[key] === 'number') {
                aggregatedData[key] = (aggregatedData[key] || 0) + newData[key];
            } else if (typeof newData[key] === 'object' && !Array.isArray(newData[key])) {
                aggregatedData[key] = this.aggregateSiteData(aggregatedData[key] || {}, newData[key]);
            } else if (Array.isArray(newData[key])) {
                aggregatedData[key] = Array.from(new Set([...(aggregatedData[key] || []), ...newData[key]]));
            }
        });

        return aggregatedData;
    }
}
